import { useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { useAutoBindGeoSheetMutation } from '@shared/api';
import { isBindingReference, type GeoAutoBindBody, type GeoNameCount } from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import {
  Check,
  FieldGrid,
  FieldLabel,
  Hint,
  LinkButton,
  Notice,
  useAction,
} from '@widgets/GeoSchemes';
import { useWizard } from './wizard';

/** Меньше двух опорных номеров — уравнивать нечего. */
const MIN_REFERENCES = 2;

const Box = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.h4`
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/* Длинная подпись на телефоне переносится, а не вылезает за блок. */
const RunButton = styled(Button)`
  max-width: 100%;
  height: auto;
  min-height: 32px;
  white-space: normal;
  text-align: center;
  text-wrap: balance;
`;

const Details = styled.details`
  summary {
    width: max-content;
    max-width: 100%;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &[open] summary {
    margin-bottom: ${({ theme }) => theme.spacing(3)};
  }
`;

const PickList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  max-height: 180px;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const PickNote = styled(Hint)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Count = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function NamePicker({
  label,
  items,
  selected,
  onChange,
}: {
  label: string;
  items: GeoNameCount[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <FieldLabel as="div">
      {label}
      <PickList role="group" aria-label={label}>
        {items.map((it) => (
          <Check
            key={it.name}
            checked={selected.includes(it.name)}
            onChange={(on) =>
              onChange(on ? [...selected, it.name] : selected.filter((x) => x !== it.name))
            }
          >
            {it.name} <Count>{it.count}</Count>
          </Check>
        ))}
      </PickList>
    </FieldLabel>
  );
}

/**
 * Автопривязка листа по номерам (шаг 2): сервер находит на листе подписанные
 * элементы, сопоставляет их по номерам с опорными проектными элементами
 * (каталог РД, ручной ввод — не извлечённые с этого же листа), уравнивает и
 * отбрасывает выбросы. Результат — обычные точки привязки (`auto:<номер>`) и
 * невязки в блоке ниже; человек их проверяет и правит.
 */
export function AutoBindPanel({
  draftPoints,
  dirty,
  onDone,
}: {
  /** Сколько точек привязки сейчас в черновике шага — их заменят найденные. */
  draftPoints: number;
  /** Есть несохранённые правки привязки. */
  dirty: boolean;
  /** Сбросить черновик шага — он подхватит точки с сервера. */
  onDone: () => void;
}) {
  const { scheme, readOnly, goTo } = useWizard();
  const [autoBind] = useAutoBindGeoSheetMutation();
  const binding = useAction();
  const [layers, setLayers] = useState<string[]>([]);
  const [blocks, setBlocks] = useState<string[]>([]);
  const [found, setFound] = useState<number | null>(null);
  /** Ответ сервера (привязка в JSON), который ещё не дошёл до карточки в кеше. */
  const [pending, setPending] = useState<{ binding: string; count: number } | null>(null);
  const bindingKey = JSON.stringify(scheme.settings.binding);

  // Черновик шага сбрасываем, только когда карточка в кеше уже с результатом:
  // RTK сообщает о записи в кеш с задержкой до кадра (autoBatch), и сброс сразу
  // после ответа мог бы перекрыться прежним черновиком. Layout — до отрисовки.
  useLayoutEffect(() => {
    if (!pending || pending.binding !== bindingKey) return;
    onDone();
    setFound(pending.count);
    setPending(null);
  }, [pending, bindingKey, onDone]);

  const sheet = scheme.sheet;
  if (readOnly || !sheet) return null;

  const elements = scheme.design.elements;
  const references = elements.filter(isBindingReference).length;
  if (references < MIN_REFERENCES) {
    const fromSheet = elements.length - references;
    return (
      <Box aria-labelledby="geo-autobind-title">
        <Title id="geo-autobind-title">Автопривязка по номерам</Title>
        <Hint>
          Нужен каталог координат РД с номерами элементов (шаг 6): программа найдёт эти номера на
          листе и сама расставит точки привязки.
          {references === 1 ? ' Сейчас подходит один элемент — нужно хотя бы два.' : ''}
          {fromSheet > 0
            ? ' Элементы, извлечённые с этого листа, не подходят: их координаты посчитаны через текущую привязку.'
            : ''}
        </Hint>
        <div>
          <LinkButton type="button" onClick={() => goTo(6)}>
            Загрузить каталог на шаге 6 →
          </LinkButton>
        </div>
      </Box>
    );
  }

  const isDxf = sheet.kind === 'dxf';
  const hasPickers = isDxf && (sheet.blocks.length > 0 || sheet.layers.length > 0);
  const picked = layers.length + blocks.length;

  const run = async () => {
    if (
      draftPoints > 0 &&
      !window.confirm(
        dirty
          ? 'Несохранённые точки привязки заменятся найденными автоматически. Продолжить?'
          : 'Текущие точки привязки заменятся найденными автоматически. Продолжить?',
      )
    )
      return;
    setFound(null);
    setPending(null);
    const body: GeoAutoBindBody = isDxf
      ? {
          mode: 'dxf_entities',
          ...(layers.length > 0 ? { layers } : {}),
          ...(blocks.length > 0 ? { blocks } : {}),
        }
      : { mode: 'piles_pdf' };
    const res = await binding.runValue(
      () => autoBind({ id: scheme.id, ...body }).unwrap(),
      'Автопривязка не выполнена.',
    );
    if (res) {
      setPending({
        binding: JSON.stringify(res.settings.binding),
        count: res.settings.binding.control_points.length,
      });
    }
  };

  return (
    <Box aria-labelledby="geo-autobind-title">
      <Head>
        <Title id="geo-autobind-title">Автопривязка по номерам</Title>
        <RunButton
          type="button"
          $variant="secondary"
          $size="sm"
          onClick={() => void run()}
          disabled={binding.busy}
        >
          {binding.busy ? 'Ищем номера на листе…' : 'Привязать автоматически по номерам'}
        </RunButton>
      </Head>
      <Hint>
        Опорных элементов с номерами (каталог РД, ручной ввод): {references}. Программа найдёт их
        подписи на листе, уравняет и отбросит выбросы — до 12 точек привязки. Числа считает
        программа.
      </Hint>
      {hasPickers && (
        <Details>
          <summary>
            Уточнить блоки и слои (необязательно){picked > 0 ? ` · выбрано ${picked}` : ''}
          </summary>
          <FieldGrid $min={200}>
            {sheet.blocks.length > 0 && (
              <NamePicker
                label="Блоки DXF"
                items={sheet.blocks}
                selected={blocks}
                onChange={setBlocks}
              />
            )}
            {sheet.layers.length > 0 && (
              <NamePicker
                label="Слои DXF"
                items={sheet.layers}
                selected={layers}
                onChange={setLayers}
              />
            )}
          </FieldGrid>
          <PickNote>
            Ничего не выбрано — программа ищет подписанные элементы по всему листу.
          </PickNote>
        </Details>
      )}
      {binding.error && (
        <Notice $tone="danger" role="alert">
          {binding.error}
        </Notice>
      )}
      {found != null && !binding.error && (
        <Notice $tone="success" role="status">
          Точки привязки расставлены автоматически: {found}. Невязки и RMS — ниже; лишнюю точку
          можно удалить и сохранить шаг.
        </Notice>
      )}
    </Box>
  );
}
